import React, { useEffect, useRef, useState } from 'react';

interface AnimatedNumberProps {
  value: number;
  duration?: number; // ms
  suffix?: string;
  prefix?: string;
  decimals?: number;
  formatLargeNumbers?: boolean;
}

const formatNumber = (num: number, decimals: number = 0): string => {
  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(decimals) + 'B';
  } else if (num >= 1000000) {
    return (num / 1000000).toFixed(decimals) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(decimals) + 'K';
  }
  return num.toFixed(decimals);
};

export const AnimatedNumber: React.FC<AnimatedNumberProps> = ({ 
  value, 
  duration = 1800, 
  suffix = '', 
  prefix = '', 
  decimals = 0,
  formatLargeNumbers = false 
}) => {
  const [display, setDisplay] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const start = useRef<number>();

  useEffect(() => {
    let observer: IntersectionObserver;
    let frame: number;
    const animate = (timestamp: number) => {
      if (!start.current) start.current = timestamp;
      const progress = Math.min((timestamp - start.current) / duration, 1);
      const current = Math.floor(progress * (value * Math.pow(10, decimals))) / Math.pow(10, decimals);
      setDisplay(current);
      if (progress < 1) {
        frame = requestAnimationFrame(animate);
      } else {
        setDisplay(value);
      }
    };
    if (ref.current && !hasAnimated) {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setHasAnimated(true);
            requestAnimationFrame(animate);
            observer.disconnect();
          }
        },
        { threshold: 0.2 }
      );
      observer.observe(ref.current);
    }
    return () => {
      if (observer && observer.disconnect) observer.disconnect();
      cancelAnimationFrame(frame);
      start.current = undefined;
    };
    // eslint-disable-next-line
  }, [value, duration, decimals]);

  const displayValue = hasAnimated ? display : 0;
  const formattedValue = formatLargeNumbers 
    ? formatNumber(displayValue, decimals)
    : displayValue.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

  return (
    <span ref={ref}>
      {prefix}
      {' '}{formattedValue}{' '}
      {suffix}
    </span>
  );
}; 